import React, { useEffect, useLayoutEffect, useState, useContext } from 'react'

import LoadingContext from '../../../context/Loading/Loading'
import EventDetailsContext from '../../../context/EventDetails/EventDetails'

import { useSheet } from '../../../providers/SideSheetProvider/SideSheetProvider'
import { useModal } from '../../../providers/ModalProvider/ModalProvider'

import { getEvent } from '../../../utilities/api'

import useSafeAsync from '../../../utilities/useSafeAsync'

import { PageLoadingContainer } from '../../PageLoadingContainer'
import { ExchangeTicketsMap } from './ExchangeTicketsMap'
import { Navigation } from './Navigation'
import { CurrentSelected } from '../../../providers/SideSheetProvider/SheetContent/CurrentSelected'
import { SelectGASeats } from '../../ModalContent'

export default function ExchangeTicketsSeatMapWrapper({
  eventId,
  fromOrder,
  fromEvent,
  fromTickets,
}) {
  const safeSetState = useSafeAsync()

  const { openModal, closeAllModals } = useModal()

  const { openSheet, closeSheet } = useSheet()

  const { isLoading, showLoading, hideLoading } = useContext(LoadingContext)

  const { setSelectedAssignSeats } = useContext(EventDetailsContext)

  const [event, setEvent] = useState()

  const [data, setData] = useState(null)

  const [background, setBackground] = useState(null)

  // Might not be so important for packagesMap but previously the ds for this was like:
  // { seats: {seatIds}, gaSeats: { sectionId: [seatIds]}}
  const [selectedSeatIds, setSelectedSeatIds] = useState({})
  const [soldMap, setSoldMap] = useState(new Map())
  const [isMapDisabled, setIsMapDisabled] = useState(false)
  const [isAnotherModalOpen, setIsAnotherModalOpen] = useState(false)

  useLayoutEffect(() => {
    let mapModal
    if (isAnotherModalOpen) {
      mapModal = document.getElementById('exchange-map-modal')
      mapModal.classList.add('modal-opac')
    }

    return () => {
      if (mapModal) mapModal.classList.remove('modal-opac')
    }
  }, [isAnotherModalOpen])

  useEffect(() => {
    showLoading()
    if (eventId) loadEvent()
  }, [eventId])

  useEffect(() => {
    // Toggle sideSheet based on selected state
    if (selectedSeatIds && Object.keys(selectedSeatIds).length > 0) {
      openSheet(CurrentSelected, {
        selectedSeatIds: selectedSeatIds,
        handleRemove: clearSelectedSeats,
      })
    } else {
      closeSheet()
    }
  }, [selectedSeatIds])

  // controls if seatmap can be changed
  useEffect(() => {
    setIsMapDisabled(event?.soldOut)
  }, [event])

  const loadEvent = async () => {
    try {
      const res = await getEvent(eventId)

      safeSetState(() => {
        setEvent(res?.data)
        setData(res?.data?.seatmap?.mapping)
        setBackground(res?.data?.seatmap?.background)

        storeSoldTicketsInMap(res?.data)
      })
    } catch (err) {
      console.error(err)
    } finally {
      hideLoading()
    }
  }

  const storeSoldTicketsInMap = (event) => {
    // Use reduce to build the single map
    const finalSoldMap = event.tickets.reduce((accMap, ticket) => {
      // Treat ANY non-available ticket as unavailable on the seatmap.
      // This prevents selecting seats that are in states like inCart/unavailable/etc.
      if (ticket?.on_sale_status && ticket.on_sale_status !== 'available') {
        const isGeneralAdmission = ticket.generalAdmission
        if (isGeneralAdmission) {
          const sectionId = ticket.sectionId
          const seatId = ticket.seatId // Still need the seatId for the array

          if (!sectionId) {
            console.warn('Sold GA ticket missing sectionId:', ticket)
            return accMap // Skipping ticket if sectionId is missing
          }

          if (!seatId) {
            console.warn('Sold GA ticket missing seatId:', ticket)
            return accMap // Skipping ticket if seatId is missing
          }

          // Get the existing array for this section or initialize a new one
          // We access the accumulator map directly using the sectionId as the key
          const sectionSeats = accMap.get(sectionId) || []

          if (!Array.isArray(sectionSeats) && accMap.has(sectionId)) {
            console.error(
              `Collision or unexpected data type for key ${sectionId}. Expected array for GA section.`
            )
            return accMap // Skip this ticket to avoid corrupting map
          }

          sectionSeats.push(seatId)
          accMap.set(sectionId, sectionSeats) // Use sectionId as the key
        } else {
          const seatId = ticket.seatId
          if (!seatId) {
            console.warn('Sold reserved ticket missing seatId:', ticket)
            return accMap // Skipping ticket if seatId is missing
          }

          if (accMap.has(seatId) && Array.isArray(accMap.get(seatId))) {
            console.error(
              `Collision for key ${seatId}. It's already used as a GA section ID.`
            )
            return accMap // Skip this ticket to avoid corrupting map
          }

          // Use the reserved seatId as the key, mapping to true
          accMap.set(seatId, true)
        }
      }
      // Return the accumulator map for the next ticket
      return accMap
    }, new Map()) // Start with a new empty Map

    setSoldMap(finalSoldMap)
  }

  const saveSeatSelections = () => {
    setSelectedAssignSeats(selectedSeatIds)
  }

  const handleGoBack = () => {
    closeAllModals()
  }

  const clearSelectedSeats = () => {
    setSelectedSeatIds({})
  }

  const checkDisabled = () => {
    if (!selectedSeatIds?.seats && !selectedSeatIds?.gaSeats) return true
    return false
  }

  const openGAModal = (section) => {
    const totalSoldSeats = soldMap.get(section.sectionId)?.length || 0
    const seatsToAssignFrom = section.spots.slice(totalSoldSeats)
    setIsAnotherModalOpen(true)
    openModal(
      SelectGASeats,
      {
        setSelectedSeatIds: setSelectedSeatIds,
        section: section,
        seatsToAssignFrom: seatsToAssignFrom,
        onClose: () => setIsAnotherModalOpen(false),
        // Keep the exchange map open underneath this picker.
        dismissMode: 'top',
      },
      { className: '', contentClassName: '' },
      { replace: false }
    )
  }

  return (
    <>
      {isLoading ? (
        <PageLoadingContainer style="without-sidebar" />
      ) : (
        <>
          <Navigation
            event={event}
            configuration={event?.seatmap}
            isMapDisabled={isMapDisabled}
            selectedSeatIds={selectedSeatIds}
            saveSeatSelections={saveSeatSelections}
            handleGoBack={handleGoBack}
            isDisabled={checkDisabled()}
            fromOrder={fromOrder}
            fromEvent={fromEvent}
            fromTickets={fromTickets}
            setIsAnotherModalOpen={setIsAnotherModalOpen}
          />
          {data && background && (
            <ExchangeTicketsMap
              data={data}
              background={background}
              selectedSeatIds={selectedSeatIds}
              setSelectedSeatIds={setSelectedSeatIds}
              isMapDisabled={isMapDisabled}
              soldMap={soldMap}
              openGAModal={openGAModal}
              event={event}
            />
          )}
        </>
      )}
    </>
  )
}
